import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Receipt, TrendingUp, FileDown, Smartphone } from "lucide-react";
import { exportCostsToPDF } from "@/lib/pdfExport";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface EmployeeOtherCostsTabProps {
  employeeId: string;
  employeeName?: string;
  readOnly?: boolean;
}

interface CostCategory {
  id: string;
  name: string;
  category_type: string;
  is_recurring: boolean;
  default_frequency: string;
}

interface CostEntry {
  id: string;
  date: string;
  cost_type: string;
  amount: number;
  currency: string;
  payment_frequency: string;
  description: string;
  license_plate?: string;
  vehicle_model?: string;
  receipt_number?: string;
  notes?: string;
  vehicle_id?: string;
}

interface MobileDevice {
  id: string;
  phone_type: string;
  model: string | null;
  purchase_cost: number | null;
}

interface SimCard {
  id: string;
  sim_number: string;
  phone_number: string | null;
  provider: string;
  tariff: string | null;
  monthly_cost: number | null;
}

const FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monatlich" },
  { value: "quarterly", label: "Vierteljährlich (3 Monate)" },
  { value: "half_yearly", label: "Halbjährlich (6 Monate)" },
  { value: "yearly", label: "Jährlich" },
  { value: "one_time", label: "Einmalig" },
];

export function EmployeeOtherCostsTab({ employeeId, employeeName = "Mitarbeiter", readOnly = false }: EmployeeOtherCostsTabProps) {
  const { user } = useAuth();
  const [costs, setCosts] = useState<CostEntry[]>([]);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [mobileDevice, setMobileDevice] = useState<MobileDevice | null>(null);
  const [simCards, setSimCards] = useState<SimCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<CostEntry>>({
    currency: "EUR",
    payment_frequency: "monthly",
  });

  useEffect(() => {
    if (user && employeeId) {
      loadData();
    }
  }, [user, employeeId]);

  const loadData = async () => {
    try {
      // Load categories
      const { data: cats, error: catError } = await supabase
        .from("cost_categories")
        .select("*")
        .order("sort_order");

      if (catError) throw catError;
      setCategories(cats || []);

      // Load costs (using existing vehicle_costs table) - excluding vehicle-linked costs
      const { data: costsData, error: costsError } = await supabase
        .from("vehicle_costs")
        .select("*")
        .eq("employee_id", employeeId)
        .is("vehicle_id", null)
        .order("date", { ascending: false });

      if (costsError) throw costsError;
      setCosts(costsData || []);

      // Load assigned mobile device
      const { data: deviceData, error: deviceError } = await supabase
        .from("mobile_phones")
        .select("id, phone_type, model, purchase_cost")
        .eq("assigned_employee_id", employeeId)
        .eq("is_active", true)
        .maybeSingle();

      if (deviceError) throw deviceError;
      setMobileDevice(deviceData);

      // Load assigned SIM cards
      const { data: simData, error: simError } = await supabase
        .from("sim_cards")
        .select("id, sim_number, phone_number, provider, tariff, monthly_cost")
        .eq("assigned_employee_id", employeeId)
        .eq("is_active", true);

      if (simError) throw simError;
      setSimCards(simData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden der Kosten");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    const defaultCategory = categories[0];
    setFormData({
      currency: "EUR",
      cost_type: defaultCategory?.name || "",
      payment_frequency: "monthly",
    });
    setDialogOpen(true);
  };

  const handleEdit = (entry: CostEntry) => {
    setEditingId(entry.id);
    setFormData(entry);
    setDialogOpen(true);
  };

  const handleCategoryChange = (categoryName: string) => {
    const category = categories.find((c) => c.name === categoryName);
    setFormData({
      ...formData,
      cost_type: categoryName,
      payment_frequency: category?.is_recurring ? (category.default_frequency || "monthly") : "one_time",
    });
  };

  const handleSave = async () => {
    if (!formData.date || !formData.amount || !formData.cost_type) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from("vehicle_costs")
          .update({
            date: formData.date,
            cost_type: formData.cost_type,
            amount: formData.amount,
            currency: formData.currency || "EUR",
            license_plate: formData.license_plate,
            vehicle_model: formData.vehicle_model,
            payment_frequency: formData.payment_frequency || "monthly",
            description: formData.description,
            receipt_number: formData.receipt_number,
            notes: formData.notes,
          })
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Kosten aktualisiert");
      } else {
        const { error } = await supabase.from("vehicle_costs").insert([
          {
            employee_id: employeeId,
            date: formData.date!,
            cost_type: formData.cost_type!,
            amount: formData.amount!,
            currency: formData.currency || "EUR",
            license_plate: formData.license_plate,
            vehicle_model: formData.vehicle_model,
            payment_frequency: formData.payment_frequency || "monthly",
            description: formData.description,
            receipt_number: formData.receipt_number,
            notes: formData.notes,
            created_by: user!.id,
          },
        ]);

        if (error) throw error;
        toast.success("Kosten gespeichert");
      }

      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error saving costs:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Möchten Sie diesen Eintrag wirklich löschen?")) return;

    try {
      const { error } = await supabase.from("vehicle_costs").delete().eq("id", id);

      if (error) throw error;
      toast.success("Eintrag gelöscht");
      loadData();
    } catch (error) {
      console.error("Error deleting costs:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    const option = FREQUENCY_OPTIONS.find(o => o.value === frequency);
    return option?.label || frequency;
  };

  const getFrequencyBadgeVariant = (frequency: string): "default" | "secondary" | "outline" => {
    switch (frequency) {
      case "one_time":
        return "outline";
      case "monthly":
        return "default";
      default:
        return "secondary";
    }
  };

  const calculateMonthlyTotal = () => {
    let total = costs.reduce((sum, cost) => {
      const amount = cost.amount || 0;
      switch (cost.payment_frequency) {
        case "monthly":
          return sum + amount;
        case "quarterly":
          return sum + amount / 3;
        case "half_yearly":
          return sum + amount / 6;
        case "yearly":
          return sum + amount / 12;
        default:
          return sum; // one_time costs not included in monthly
      }
    }, 0);
    
    // Add SIM card monthly costs
    simCards.forEach(sim => {
      if (sim.monthly_cost) {
        total += sim.monthly_cost;
      }
    });
    
    return total;
  };

  const calculateYearlyTotal = () => {
    let total = costs.reduce((sum, cost) => {
      const amount = cost.amount || 0;
      switch (cost.payment_frequency) {
        case "monthly":
          return sum + amount * 12;
        case "quarterly":
          return sum + amount * 4;
        case "half_yearly":
          return sum + amount * 2;
        case "yearly":
          return sum + amount;
        case "one_time":
          return sum + amount; // Include one-time in yearly total
        default:
          return sum;
      }
    }, 0);
    
    // Add SIM card costs (monthly * 12)
    simCards.forEach(sim => {
      if (sim.monthly_cost) {
        total += sim.monthly_cost * 12;
      }
    });
    
    // Add device purchase cost
    if (mobileDevice?.purchase_cost) {
      total += mobileDevice.purchase_cost;
    }
    
    return total;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const monthlyTotal = calculateMonthlyTotal();
  const yearlyTotal = calculateYearlyTotal();

  return (
    <>
      {/* Gesamtkosten-Zusammenfassung */}
      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Monatliche Kosten (wiederkehrend)</p>
                <p className="text-2xl font-bold text-primary">
                  {monthlyTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Jahreskosten (gesamt)</p>
              <p className="text-xl font-semibold">
                {yearlyTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sonstige Kosten */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Receipt className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Sonstige Kosten</CardTitle>
                <CardDescription>
                  Wiederkehrende und einmalige Ausgaben (Tools, Lizenzen, etc.)
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => exportCostsToPDF(employeeName, costs, monthlyTotal)}>
                <FileDown className="mr-2 h-4 w-4" />
                PDF
              </Button>
              {!readOnly && (
                <Button onClick={handleAdd}>
                  <Plus className="mr-2 h-4 w-4" />
                  Neue Kosten
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {costs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[100px]">Datum</TableHead>
                  <TableHead className="min-w-[150px]">Kategorie</TableHead>
                  <TableHead className="min-w-[100px]">Betrag</TableHead>
                  <TableHead className="min-w-[140px]">Frequenz</TableHead>
                  <TableHead className="min-w-[100px]">Mtl. Kosten</TableHead>
                  <TableHead className="min-w-[150px]">Beschreibung</TableHead>
                  {!readOnly && <TableHead className="min-w-[100px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {costs.map((entry) => {
                  // Calculate monthly equivalent
                  let monthlyEquivalent = 0;
                  switch (entry.payment_frequency) {
                    case "monthly":
                      monthlyEquivalent = entry.amount;
                      break;
                    case "quarterly":
                      monthlyEquivalent = entry.amount / 3;
                      break;
                    case "half_yearly":
                      monthlyEquivalent = entry.amount / 6;
                      break;
                    case "yearly":
                      monthlyEquivalent = entry.amount / 12;
                      break;
                  }

                  return (
                    <TableRow key={entry.id}>
                      <TableCell>
                        {format(new Date(entry.date), "dd.MM.yyyy", { locale: de })}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{entry.cost_type}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {entry.amount.toLocaleString("de-DE", {
                          style: "currency",
                          currency: entry.currency,
                        })}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getFrequencyBadgeVariant(entry.payment_frequency || "monthly")}>
                          {getFrequencyLabel(entry.payment_frequency || "monthly")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {entry.payment_frequency === "one_time" 
                          ? "-" 
                          : monthlyEquivalent.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {entry.description || "-"}
                      </TableCell>
                      {!readOnly && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(entry)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(entry.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">Noch keine Kosten vorhanden</p>
          )}
        </CardContent>
      </Card>

      {/* Zugewiesenes Handy & SIM-Karten */}
      {(mobileDevice || simCards.length > 0) && (
        <Card className="mt-4">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Smartphone className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Zugewiesenes Firmenhandy & SIM-Karten</CardTitle>
                <CardDescription>Kosten für Ihre Mobilgeräte</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {mobileDevice && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-medium mb-2">Gerät</p>
                <div className="flex justify-between">
                  <span>{mobileDevice.phone_type} {mobileDevice.model && `(${mobileDevice.model})`}</span>
                  <span className="font-medium">
                    {mobileDevice.purchase_cost 
                      ? mobileDevice.purchase_cost.toLocaleString("de-DE", { style: "currency", currency: "EUR" }) + " (Anschaffung)"
                      : "-"}
                  </span>
                </div>
              </div>
            )}
            {simCards.length > 0 && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-medium mb-2">SIM-Karten</p>
                {simCards.map(sim => (
                  <div key={sim.id} className="flex justify-between py-1">
                    <span>{sim.provider} - {sim.phone_number || sim.sim_number} {sim.tariff && `(${sim.tariff})`}</span>
                    <span className="font-medium text-primary">
                      {sim.monthly_cost 
                        ? sim.monthly_cost.toLocaleString("de-DE", { style: "currency", currency: "EUR" }) + "/Monat"
                        : "-"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog: Sonstige Kosten hinzufügen/bearbeiten */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Kosten bearbeiten" : "Kosten hinzufügen"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={formData.date || ""}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Kategorie *</Label>
                <Select value={formData.cost_type || ""} onValueChange={handleCategoryChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kategorie wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Betrag (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: parseFloat(e.target.value) })
                  }
                />
              </div>
              <div>
                <Label>Häufigkeit *</Label>
                <Select
                  value={formData.payment_frequency || "monthly"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, payment_frequency: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Frequency explanation */}
            <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
              <strong>Hinweis:</strong> Wiederkehrende Kosten werden automatisch berechnet:
              <ul className="mt-1 ml-4 list-disc">
                <li><strong>Monatlich:</strong> Jeden Monat fällig</li>
                <li><strong>Vierteljährlich:</strong> Alle 3 Monate fällig</li>
                <li><strong>Halbjährlich:</strong> Alle 6 Monate fällig</li>
                <li><strong>Jährlich:</strong> Einmal pro Jahr fällig</li>
                <li><strong>Einmalig:</strong> Nur einmal (nicht wiederkehrend)</li>
              </ul>
            </div>

            <div>
              <Label>Beschreibung</Label>
              <Textarea
                rows={2}
                value={formData.description || ""}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="z.B. App-Lizenz, Handygebühren"
              />
            </div>

            <div>
              <Label>Belegnummer</Label>
              <Input
                value={formData.receipt_number || ""}
                onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
                placeholder="z.B. RE-2024-001"
              />
            </div>

            <div>
              <Label>Notizen</Label>
              <Textarea
                rows={2}
                value={formData.notes || ""}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
